import React, { useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Divider,
  Menu,
  MenuItem,
  Avatar,
  Chip,
  ListSubheader,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { BRAND } from '../config/brand';
import { layoutTokens } from '../theme/theme';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Description as DocumentIcon,
  CloudUpload as UploadIcon,
  Search as SearchIcon,
  Chat as ChatIcon,
  Article as ArticleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  Code as CodeIcon,
  Logout as LogoutIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';

const { drawerWidth } = layoutTokens;

interface LayoutProps {
  children: React.ReactNode;
  onLogout?: () => void;
  user?: any;
}

interface NavItem {
  text: string;
  icon: React.ReactElement;
  path: string;
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const PAGE_TITLES: Record<string, string> = {
  '/': '仪表盘',
  '/documents': '文档列表',
  '/upload': '上传文档',
  '/search': '知识搜索',
  '/chat': '智能问答',
  '/wechat-article': '公众号文章',
  '/users': '用户管理',
  '/departments': '部门管理',
  '/api-test': 'API 测试',
};

const Layout: React.FC<LayoutProps> = ({ children, onLogout, user }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();

  const navSections = useMemo((): NavSection[] => {
    const sections: NavSection[] = [
      {
        title: '工作台',
        items: [{ text: '仪表盘', icon: <DashboardIcon />, path: '/' }],
      },
      {
        title: '知识库',
        items: [
          { text: '文档列表', icon: <DocumentIcon />, path: '/documents' },
          { text: '上传文档', icon: <UploadIcon />, path: '/upload' },
          { text: '知识搜索', icon: <SearchIcon />, path: '/search' },
          { text: '智能问答', icon: <ChatIcon />, path: '/chat' },
          { text: '公众号文章', icon: <ArticleIcon />, path: '/wechat-article' },
        ],
      },
    ];

    if (user?.role === 'SUPER_ADMIN') {
      sections.push({
        title: '系统管理',
        items: [
          { text: '用户管理', icon: <PersonIcon />, path: '/users' },
          { text: '部门管理', icon: <BusinessIcon />, path: '/departments' },
          { text: 'API 测试', icon: <CodeIcon />, path: '/api-test' },
        ],
      });
    }

    return sections;
  }, [user?.role]);

  const currentPageTitle = useMemo(() => {
    if (PAGE_TITLES[location.pathname]) {
      return PAGE_TITLES[location.pathname];
    }
    if (location.pathname.startsWith('/documents/')) {
      return '文档详情';
    }
    return BRAND.shortName;
  }, [location.pathname]);

  const roleLabel =
    user?.role === 'SUPER_ADMIN' ? '超级管理员' : user?.role === 'ADMIN' ? '管理员' : '用户';

  const roleColor =
    user?.role === 'SUPER_ADMIN' ? 'error' : user?.role === 'ADMIN' ? 'warning' : 'default';

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', bgcolor: layoutTokens.sidebarBg }}>
      <Box
        sx={{
          px: 2.5,
          py: 2.5,
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
            color: 'primary.main',
          }}
        >
          <MenuBookIcon />
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" noWrap sx={{ fontWeight: 700, lineHeight: 1.2 }}>
            {BRAND.shortName}
          </Typography>
          <Typography variant="caption" color="text.secondary" noWrap>
            企业知识中台
          </Typography>
        </Box>
      </Box>

      <Box sx={{ flex: 1, overflowY: 'auto', py: 1 }}>
        {navSections.map((section) => (
          <List
            key={section.title}
            subheader={
              <ListSubheader
                component="div"
                sx={{
                  bgcolor: 'transparent',
                  lineHeight: '32px',
                  fontSize: '0.6875rem',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  color: layoutTokens.navSectionColor,
                  px: 2.5,
                }}
              >
                {section.title}
              </ListSubheader>
            }
            disablePadding
          >
            {section.items.map((item) => {
              const selected =
                location.pathname === item.path ||
                (item.path !== '/' && location.pathname.startsWith(item.path));

              return (
                <ListItemButton
                  key={item.path}
                  selected={selected}
                  onClick={() => {
                    navigate(item.path);
                    setMobileOpen(false);
                  }}
                  sx={{
                    mx: 1,
                    '&.Mui-selected': {
                      borderLeft: '3px solid',
                      borderColor: 'primary.main',
                      pl: '13px',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 36 }}>{item.icon}</ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    primaryTypographyProps={{
                      fontSize: '0.9375rem',
                      fontWeight: selected ? 600 : 500,
                    }}
                  />
                </ListItemButton>
              );
            })}
          </List>
        ))}
      </Box>

      {user && (
        <Box sx={{ p: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: 'primary.main',
                fontSize: '0.875rem',
                fontWeight: 600,
              }}
            >
              {(user.fullName || user.username || '?').charAt(0).toUpperCase()}
            </Avatar>
            <Box sx={{ minWidth: 0, flex: 1 }}>
              <Typography variant="body2" noWrap sx={{ fontWeight: 600 }}>
                {user.fullName || user.username}
              </Typography>
              <Chip label={roleLabel} color={roleColor as any} size="small" sx={{ height: 20, mt: 0.25 }} />
            </Box>
          </Box>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: layoutTokens.contentBg }}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          bgcolor: layoutTokens.topbarBg,
          color: 'text.primary',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 }, px: { xs: 2, sm: 3 } }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={() => setMobileOpen(!mobileOpen)}
            sx={{ mr: 1.5, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="h6" noWrap sx={{ fontWeight: 600, fontSize: '1.0625rem' }}>
              {currentPageTitle}
            </Typography>
          </Box>

          {user && (
            <IconButton onClick={(e) => setAnchorEl(e.currentTarget)} sx={{ ml: 1 }}>
              <Avatar
                sx={{
                  width: 34,
                  height: 34,
                  bgcolor: 'primary.main',
                  fontSize: '0.875rem',
                }}
              >
                {(user.fullName || user.username || '?').charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          )}
        </Toolbar>
      </AppBar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        PaperProps={{
          sx: { minWidth: 200, mt: 1, borderRadius: 2, border: '1px solid', borderColor: 'divider' },
        }}
      >
        <MenuItem disabled sx={{ opacity: '1 !important' }}>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {user?.fullName || user?.username}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {roleLabel}
            </Typography>
          </Box>
        </MenuItem>
        <Divider />
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onLogout?.();
          }}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          退出登录
        </MenuItem>
      </Menu>

      <Box component="nav" sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}>
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={() => setMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              borderRight: '1px solid',
              borderColor: 'divider',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          minHeight: '100vh',
        }}
      >
        <Toolbar sx={{ minHeight: { xs: 56, sm: 64 } }} />
        <Box sx={{ px: { xs: 2, sm: 3 }, py: 3, maxWidth: 1280, mx: 'auto', width: '100%' }}>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
